'use client';
import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useWebSocket } from '@/hooks/useWebSocket';
import FloatingChatWindow from './FloatingChatWindow';

interface Contact {
  _id: string;
  firstName: string;
  lastName: string;
  name: string;
  email: string;
  profilePic: string;
  department: string;
  designation: string;
  status: string;
  isOnline: boolean;
  allowChat: boolean;
}

interface FloatingWindow {
  id: string;
  contact: Contact;
  chatId: string;
  isMinimized: boolean;
  position: number;
}

/**
 * Returns the maximum number of non-minimized windows based on viewport width.
 * Desktop (>1024px): 3, Tablet (768-1024px): 2, Mobile (<768px): 1
 */
function getMaxWindows(width: number): number {
  if (width > 1024) return 3;
  if (width >= 768) return 2;
  return 1;
}

export default function ChatWindowManager() {
  const { user } = useAuth();
  const { onlineUsers } = useWebSocket();
  const [floatingWindows, setFloatingWindows] = useState<FloatingWindow[]>([]);
  const [windowCounter, setWindowCounter] = useState(0);
  const [viewportWidth, setViewportWidth] = useState<number>(
    typeof window !== 'undefined' ? window.innerWidth : 1200
  );

  const windowCounterRef = useRef<number>(windowCounter);
  windowCounterRef.current = windowCounter;

  // Track viewport width on resize
  useEffect(() => {
    const handleResize = () => {
      setViewportWidth(window.innerWidth);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // When viewport shrinks, enforce the new max by minimizing excess windows
  useEffect(() => {
    const maxWindows = getMaxWindows(viewportWidth);
    setFloatingWindows((prev) => {
      const nonMinimized = prev.filter((w) => !w.isMinimized);
      if (nonMinimized.length <= maxWindows) return prev;

      // Minimize the oldest non-minimized windows until we meet the limit
      const excessCount = nonMinimized.length - maxWindows;
      const toMinimize = nonMinimized.slice(0, excessCount).map((w) => w.id);

      return prev.map((w) =>
        toMinimize.includes(w.id) ? { ...w, isMinimized: true } : w
      );
    });
  }, [viewportWidth]);

  // Update contact online status in floating windows when onlineUsers changes
  useEffect(() => {
    setFloatingWindows((prev) =>
      prev.map((window) => ({
        ...window,
        contact: {
          ...window.contact,
          isOnline: onlineUsers.includes(window.contact._id),
        },
      }))
    );
  }, [onlineUsers]);

  // Open a new chat window or restore an existing one
  const openChatWindow = useCallback(
    (contact: Contact, chatId: string) => {
      setFloatingWindows((prev) => {
        // Check if window already exists for this contact
        const existingIndex = prev.findIndex((w) => w.contact._id === contact._id);
        if (existingIndex !== -1) {
          // Unminimize the existing window
          return prev.map((w) =>
            w.contact._id === contact._id ? { ...w, isMinimized: false } : w
          );
        }

        // Create new window
        const newWindow: FloatingWindow = {
          id: `window-${windowCounterRef.current}`,
          contact: {
            ...contact,
            isOnline: onlineUsers.includes(contact._id),
          },
          chatId,
          isMinimized: false,
          position: prev.filter((w) => !w.isMinimized).length,
        };

        setWindowCounter((c) => c + 1);

        const updated = [...prev, newWindow];

        // Enforce max windows: minimize the oldest non-minimized if we exceed the limit
        const maxWindows = getMaxWindows(
          typeof window !== 'undefined' ? window.innerWidth : 1200
        );
        const nonMinimized = updated.filter((w) => !w.isMinimized);

        if (nonMinimized.length > maxWindows) {
          const excessCount = nonMinimized.length - maxWindows;
          // Minimize the oldest non-minimized (first in list), excluding the newest
          const toMinimize = nonMinimized
            .slice(0, excessCount)
            .map((w) => w.id);

          return updated.map((w) =>
            toMinimize.includes(w.id) ? { ...w, isMinimized: true } : w
          );
        }

        return updated;
      });
    },
    [onlineUsers]
  );

  // Listen for 'openChatWindow' CustomEvent from other components
  useEffect(() => {
    const handleOpenChatWindow = (event: CustomEvent) => {
      const { contact, chatId } = event.detail;
      openChatWindow(contact, chatId);
    };

    window.addEventListener('openChatWindow', handleOpenChatWindow as EventListener);
    return () => {
      window.removeEventListener('openChatWindow', handleOpenChatWindow as EventListener);
    };
  }, [openChatWindow]);

  const closeChatWindow = useCallback((windowId: string) => {
    setFloatingWindows((prev) => prev.filter((w) => w.id !== windowId));
  }, []);

  const minimizeChatWindow = useCallback((windowId: string) => {
    setFloatingWindows((prev) =>
      prev.map((w) =>
        w.id === windowId ? { ...w, isMinimized: !w.isMinimized } : w
      )
    );
  }, []);

  if (!user) return null;

  // Separate non-minimized and minimized windows
  const nonMinimizedWindows = floatingWindows.filter((w) => !w.isMinimized);
  const minimizedWindows = floatingWindows.filter((w) => w.isMinimized);
  const isMobile = viewportWidth < 768;

  return (
    <>
      {/* Non-minimized floating chat windows, stacked right to left */}
      {nonMinimizedWindows.map((fw, index) => (
        <FloatingChatWindow
          key={fw.id}
          contact={fw.contact}
          chatId={fw.chatId}
          position={index}
          isMinimized={false}
          onClose={() => closeChatWindow(fw.id)}
          onMinimize={() => minimizeChatWindow(fw.id)}
        />
      ))}

      {/* Minimized windows as compact tabs at viewport bottom */}
      {minimizedWindows.length > 0 && (
        <div
          className="fixed bottom-0 flex items-end gap-1 z-30"
          style={{
            right: isMobile
              ? '10px'
              : `${80 + nonMinimizedWindows.length * 340}px`,
          }}
        >
          {minimizedWindows.map((fw) => (
            <div
              key={fw.id}
              onClick={() => minimizeChatWindow(fw.id)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') minimizeChatWindow(fw.id); }}
              className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-t-lg shadow-lg hover:bg-blue-700 transition-colors text-sm font-medium max-w-[160px] cursor-pointer"
              title={`Open chat with ${fw.contact.name}`}
            >
              <span className="truncate">{fw.contact.name}</span>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  closeChatWindow(fw.id);
                }}
                className="ml-1 p-0.5 hover:bg-blue-800 rounded flex-shrink-0"
                title="Close"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
          ))}
        </div>
      )}
    </>
  );
}
